import { getLanguageLabel, type TovInput } from 'src/data/generation';
import { isLongFormContentImproverTool, isLongFormLocalizationTool } from 'src/data/generationTool';
import type { PresetToneOfVoice } from 'src/data/toneOfVoice';
import { parseJson } from 'src/lib/common';

import { LONG_FORM_ORIGINAL_DRAFT_INDEX } from './const';
import { isLongFormDraftVisible } from './drafts';
import type { LongFormDraft, LongFormProject } from './types';

export const getProjectLongFormGenerationTool = (project: LongFormProject) =>
  project?.longForm?.generationTool;

export const getProjectLongFormVisibleDrafts = (project: LongFormProject) => {
  const tool = getProjectLongFormGenerationTool(project);

  return visibleLongFormDrafts(project?.longFormBuilderDrafts, {
    // we don't want "original" draft to be displayed for "content improver" long-form project
    skipImproverOriginal: tool ? isLongFormContentImproverTool(tool) : false,
  });
};

/**
 * Returns only drafts that are in "editing"/"generating" status. Sorts them by "draftId".
 *
 * @param  drafts
 * @param [options]
 * @param [options.skipImproverOriginal] - If true, skips the "original" draft for "content improver" tool.
 * @returns
 */
export const visibleLongFormDrafts = (
  drafts: LongFormDraft[],
  options?: { skipImproverOriginal?: boolean }
) => {
  if (!drafts || !Array.isArray(drafts)) {
    return [];
  }

  const list = drafts.sort((a, b) => Number(a.draftId) - Number(b.draftId));

  if (options?.skipImproverOriginal) {
    return (
      list
        // remove the default "original" draft
        .filter((draft) => draft.draftId !== LONG_FORM_ORIGINAL_DRAFT_INDEX)
        // remove other drafts that are referenced as "original" from other drafts
        .filter((draft, _index, drafts) => {
          if (draft.originalDraftIndex != null) {
            return true;
          }
          const isOriginalDraft = drafts.some((d) => d.originalDraftIndex === draft.draftId);
          return !isOriginalDraft;
        })
        .filter(isLongFormDraftVisible)
    );
  }

  return list.filter(isLongFormDraftVisible);
};

export const isTovInputObjectEmpty = (tovInputObject: TovInput): boolean => {
  return !tovInputObject?.defaultPreset && !tovInputObject?.customId;
};

export const parseLongFormProjectTovInput = (jsonString: string): TovInput | undefined => {
  const tovInputObject = parseJson<{ default_preset?: PresetToneOfVoice; custom_id: string }>(
    jsonString
  );

  const defaultPreset = tovInputObject?.default_preset;
  const customId = tovInputObject?.custom_id;
  if (!defaultPreset && !customId) {
    return undefined;
  }

  return {
    defaultPreset,
    customId,
  };
};

export const getLongFormProjectTypeLabel = (project: LongFormProject): string | undefined => {
  const tool = getProjectLongFormGenerationTool(project);
  if (!tool) {
    return undefined;
  }

  if (isLongFormContentImproverTool(tool)) {
    return 'Long-form content improver';
  }
  if (isLongFormLocalizationTool(tool)) {
    const languages = project.longForm?.localizedLanguages || [];
    const languagesLabel = languages.map(getLanguageLabel).join(', ');
    return `Translate and localize${languagesLabel ? ` ${languagesLabel}` : ''}`;
  }

  return undefined;
};
